<template>
    <ion-page>
      <ion-header>
          <ion-toolbar>
            <ion-title>{{ group ? group.name : 'Group Results' }}</ion-title>
            <ion-buttons slot="start" @click="goBack()">
              <div class="pl-05">
                <ion-icon :icon="arrowBackOutline" size="large" />
              </div>
            </ion-buttons>
          </ion-toolbar>
      </ion-header>
      <ion-content class="menu-content">
        <ion-card v-if="group" class="list-item" color="dark">
          <ion-card-content>
            <ion-item v-if="group.dateTime" lines="none">
              <ion-icon :icon="calendarOutline" slot="start"></ion-icon>
              <ion-label>Date: {{ formatDateTime(group.dateTime) }}</ion-label>
            </ion-item>
            <ion-item v-if="group.venue" lines="none">
              <ion-icon :icon="locationOutline" slot="start"></ion-icon>
              <ion-label>Venue: {{ group.venue ? group.venue.name : 'N/A' }}</ion-label>
            </ion-item>
            <ion-item v-if="group.type" lines="none">
              <ion-icon :icon="getSportIcon(group.type)" slot="start"></ion-icon>
              <ion-label>Sport: {{ group.type }}</ion-label>
            </ion-item>

            <template v-if="group.type === 'Golf'">
              <ion-list class="mt-30 mb-2 result-wrapper" lines="none">
                <ion-item class="header-row">
                  <ion-label class="ion-text-wrap player-name">
                    <ion-text><span class="stats-header">Player</span></ion-text>
                  </ion-label>
                  <ion-label class="stats-header">
                    <ion-text><span>Total</span></ion-text>
                  </ion-label>
                  <ion-label class="stats-header" v-for="(_, index) in group.participants[0].scores" :key="index">
                    <ion-text><span>R{{ index + 1 }}</span></ion-text>
                  </ion-label>
                </ion-item>
                <ion-item v-for="participant in group.participants" :key="participant._id">
                  <ion-label class="ion-text-wrap player-name">
                    <ion-text><span class="stats">{{ participant.name }}</span></ion-text>
                  </ion-label>
                  <ion-label class="stats">
                    <ion-text><span class="stats">{{ participant.totalScore }}</span></ion-text>
                  </ion-label>
                  <ion-label class="stats" v-for="(score, index) in participant.scores" :key="index">
                    <ion-text><span class="stats">{{ score }}</span></ion-text>
                  </ion-label>
                </ion-item>
              </ion-list>
            </template>

            <!-- Race Scoring -->
            <template v-if="group.type === 'Race'">
              <ion-list class="mt-20 mb-2 result-wrapper race" lines="none">
                <ion-item class="header-row">
                  <ion-label class="place">
                    <ion-text><span class="stats-header">Pos</span></ion-text>
                  </ion-label>
                  <ion-label class="stats-header time">
                    <ion-text><span>Time</span></ion-text>
                  </ion-label>
                  <ion-label class="ion-text-wrap player-name">
                    <ion-text><span class="stats-header">Participant</span></ion-text>
                  </ion-label>
                </ion-item>
                <ion-item v-for="participant in group.participants" :key="participant._id">
                  <ion-label class="place">
                    <ion-text><span class="stats">{{ participant.position }}</span></ion-text>
                  </ion-label>
                  <ion-label class="stats time">
                    <ion-text><span class="stats">{{ participant.time }}</span></ion-text>
                  </ion-label>
                  <ion-label class="ion-text-wrap player-name">
                    <ion-text><span class="stats">{{ participant.name }}</span></ion-text>
                  </ion-label>
                </ion-item>
              </ion-list>
            </template>

            <!-- Team H2H Scoring -->
            <template v-if="group.type === 'Team H2H'">
              <ion-list class="mt-20 mb-2 result-wrapper" lines="none" v-for="match in group.matches" :key="match.id">
                <ion-item class="header-row">
                  <ion-label class="ion-text-wrap team-name">
                    <ion-text><span class="stats-header">{{ match.name }}</span></ion-text>
                  </ion-label>
                  <ion-label class="stats-header">
                    <ion-text><span>Score</span></ion-text>
                  </ion-label>
                </ion-item>
                <ion-item>
                  <ion-label class="ion-text-wrap team-name">
                    <ion-text><span class="stats">{{ match.team1.name }}</span></ion-text>
                  </ion-label>
                  <ion-label class="stats">
                    <ion-text><span class="stats">{{ match.team1.score }}</span></ion-text>
                  </ion-label>
                </ion-item>
                <ion-item>
                  <ion-label class="ion-text-wrap team-name">
                    <ion-text><span class="stats">{{ match.team2.name }}</span></ion-text>
                  </ion-label>
                  <ion-label class="stats">
                    <ion-text><span class="stats">{{ match.team2.score }}</span></ion-text>
                  </ion-label>
                </ion-item>
              </ion-list>
            </template>

            <!-- Tennis Scoring -->
            <template v-if="group.type === 'Tennis'">
              <ion-list class="mt-20 mb-2 result-wrapper" lines="none" v-for="match in group.matches" :key="match.id">
                <ion-item class="header-row">
                  <ion-label class="ion-text-wrap team-name">
                    <ion-text><span class="stats-header">{{ match.name }}</span></ion-text>
                    | <ion-text><span class="stats-header">{{ match?.venue?.name || 'Venue TBD' }}</span></ion-text>
                    | <ion-text><span class="stats-header">{{ match.dateTime ? formatDateTime(match.dateTime) : 'Date TBD' }}</span></ion-text>
                  </ion-label>
                  <ion-label class="stats-header" v-for="(_, index) in match.participants[0].setScores" :key="index">
                    <ion-text><span>Set {{ index + 1 }}</span></ion-text>
                  </ion-label>
                </ion-item>
                <ion-item v-for="(participant, index) in match.participants" :key="index">
                  <ion-label class="ion-text-wrap team-name">
                    <ion-text><span class="stats">{{ participant.name1 }} {{ participant.name2 ? `/ ${participant.name2}` : '' }} {{ getParticipantScore(match, index) }}</span></ion-text>
                  </ion-label>
                  <ion-label class="stats" v-for="(score, scoreIndex) in participant.setScores" :key="scoreIndex">
                    <ion-text><span class="stats">{{ score }}</span></ion-text>
                  </ion-label>
                </ion-item>
              </ion-list>
            </template>

            <!-- Field Event Scoring -->
            <template v-if="group.type === 'Field Event'">
              <ion-list class="mt-20 mb-2 result-wrapper field" lines="none">
                <ion-item class="header-row">
                  <ion-label class="place">
                    <ion-text><span class="stats-header">Pos</span></ion-text>
                  </ion-label>
                  <ion-label class="stats-header distance">
                    <ion-text><span>Distance/Height</span></ion-text>
                  </ion-label>
                  <ion-label class="ion-text-wrap player-name">
                    <ion-text><span class="stats-header">Participant</span></ion-text>
                  </ion-label>
                  
                </ion-item>
                <ion-item v-for="participant in group.participants" :key="participant._id">
                  <ion-label class="place">
                    <ion-text><span class="stats">{{ participant.position }}</span></ion-text>
                  </ion-label>
                  <ion-label class="stats distance">
                    <ion-text><span class="stats">{{ participant?.distanceHeight?.toFixed(2) || 0}}m</span></ion-text>
                  </ion-label>
                  <ion-label class="ion-text-wrap player-name">
                    <ion-text><span class="stats">{{ participant.name }}</span></ion-text>
                  </ion-label>
                  
                </ion-item>
              </ion-list>
            </template>

            <!-- Gymnastics Scoring -->
            <template v-if="group.type === 'Gymnastics'">
              <ion-list class="mt-20 mb-2 result-wrapper gym" lines="none">
                <ion-item class="header-row">
                  <ion-label class="place">
                    <ion-text><span class="stats-header">Pos</span></ion-text>
                  </ion-label>
                  <ion-label class="stats-header score">
                    <ion-text><span>Score</span></ion-text>
                  </ion-label>
                  <ion-label class="ion-text-wrap player-name">
                    <ion-text><span class="stats-header">Participant</span></ion-text>
                  </ion-label>
                </ion-item>
                <ion-item v-for="participant in group.participants" :key="participant._id">
                  <ion-label class="place">
                    <ion-text><span class="stats">{{ participant.position }}</span></ion-text>
                  </ion-label>
                  <ion-label class="stats score">
                    <ion-text><span class="stats">{{ participant.score }}</span></ion-text>
                  </ion-label>
                  <ion-label class="ion-text-wrap player-name">
                    <ion-text><span class="stats">{{ participant.name }}</span></ion-text>
                  </ion-label>
                </ion-item>
              </ion-list>
            </template>
          </ion-card-content>
        </ion-card>
      </ion-content>
    </ion-page>
  </template>
  
<script>
import { defineComponent } from 'vue';
import { IonContent, IonCard, IonCardHeader, IonCardTitle, IonCardSubtitle, IonCardContent, IonItem, IonLabel, IonList, IonIcon } from '@ionic/vue';
import { arrowBackOutline, calendarOutline, locationOutline } from 'ionicons/icons';
import { golfOutline, bicycleOutline, tennisballOutline, peopleOutline, ribbonOutline } from 'ionicons/icons';
import moment from 'moment';
  
export default defineComponent({
  name: 'GroupResultsDisplay',
  components: {
    IonContent, IonCard, IonCardHeader, IonCardTitle, IonCardSubtitle, IonCardContent, IonItem, IonLabel, IonList, 
  },
  data() {
    return {
      group: null,
      arrowBackOutline,
      calendarOutline,
      locationOutline,
      golfOutline,
      bicycleOutline,
      tennisballOutline,
      peopleOutline,
      ribbonOutline,
    }
  },
  async mounted() {
    await this.getGroup();
  },
  methods: {
    getParticipantScore(match, index) {
      const participant = match.participants[index];
      const otherParticipant = match.participants[index === 0 ? 1 : 0];
        
      const setsWon = (participant?.setScores || []).reduce((acc, score, idx) => {
        return acc + (Number(score) > Number(otherParticipant.setScores[idx]) ? 1 : 0);
      }, 0);
      return `(${setsWon})`;
    },
    getSportIcon(type) {
      switch (type) {
      case 'Golf':
        return this.golfOutline;
      case 'Race':
        return this.bicycleOutline;
      case 'Tennis':
        return this.tennisballOutline;
      case 'Team H2H':
        return this.peopleOutline;
      case 'Field Event':
      case 'Gymnastics':
        return this.ribbonOutline;
      default:
        return '';
      }
    },
    async getGroup() {
      const groupId = this.$route.params.id;
      try {
        const group = await this.$http.get(`/assemble/api/v1/livescores/result-groups/${groupId}`);
        this.group = group.data.data;
      } catch (error) {
        console.error('Error fetching group:', error);
      }
    },
    formatDateTime(timestamp) {
      return moment(parseInt(timestamp)).format('YYYY-MM-DD HH:mm');
    },
    goBack() {
      this.$router.back();
    }
  },
  watch: {
    // Add watcher for route changes
    '$route.params.id': {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.getGroup();
        }
      }
    }
  },
  beforeRouteUpdate(to, from, next) {
    // Reset group data before route update
    this.group = null;
    next();
  },
  
});
</script>
  
  <style lang="scss" scoped>


ion-title {
  font-family: 'BrownStd-Bold';
}

ion-grid {
  white-space: nowrap;
   --offset-bottom: auto!important;
  --overflow: hidden;
  overflow: auto;
  &::-webkit-scrollbar {
    display: none;
  }
}

.result-wrapper {
  border-radius: 1rem;
  margin-left: 1rem;
  margin-right: 1rem;
  margin-top: 1.5rem;
  padding-bottom: 1.5rem;
  -webkit-box-shadow: inset 0 0 0 1000px rgba(117, 125, 138, .25);
  box-shadow: inset 0 0 0 1000px rgba(117, 125, 138, .25);

  &.race, &.gym, &.field {
    .place { max-width: 6rem; }
    .time, .distance, .score { min-width: 10rem; max-width: 25rem; }
  }
}

.stats {
  font-family: 'BrownStd-Regular';
  font-style: normal;
  font-weight: normal;
  font-size: 0.9rem;
  line-height: 1rem;
  text-align: center;
  color: white;
  text-overflow: clip;
}

.stats-header {
  font-family: 'BrownStd-Regular';
  font-style: normal;
  font-weight: bold;
  font-size: .95rem;
  line-height: 1rem;
  text-align: center;
  color: white;
  text-overflow: clip;
  color: $secondary;
}

.player-name, .team-name {
  min-width: 6rem;
  width: 6rem;
}

.place {
  min-width: 2rem;
  width: 2rem;
}

ion-list, ion-item {
  background: transparent;
  --background: transparent;
}

.card-content-md {
  padding: 0;
}

.list-item {
  // padding: 1rem;
  // padding: 0.5rem;
  border-radius: 16px;
  font-family: 'BrownStd-Regular';
  font-size: 1.2rem;
  line-height: 137.4%;
  letter-spacing: 0.02em;
  color: white;
  // background: transparent;
  background: #171717 !important;
  &:hover {
    // background: hsl(225, 6%, 14%) !important;
  }

  margin-bottom: 1rem;

  ion-icon {
    color: white;
  }
}

.button-inner {
  color: white !important;
}

.header-bar {
  padding: 0.5rem;
}

.search-bar {
  color: #fff;
}

.round-text {
  color: #fff;
  font-family: 'Cairo';
  text-align: center;
  font-size: 14px;
  text-transform: uppercase;
  // text-decoration: underline;
  // text-decoration-color: $highlight;
}

.team-list {
  margin-top: 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  padding: 0 1.5rem;
  gap: 1.5rem;
  .team-option {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
}

ion-label {
  color: white;
  font-family: 'Cairo';
  font-size: 0.8rem;
}


ion-item {
  --padding-start: 15px;
  --padding-end: 15px;
}
.flex {
  display: flex;
}
.flex-column {
  flex-direction: column;
}
.align-center {
  align-items: center;
}
.justify-end {
  justify-content: flex-end;
}
.justify-center {
  justify-content: center;
}
.justify-between {
  justify-content: space-between;
}
.justify-around {
  justify-content: space-around;
}
.light-text {
  font-size: 14px;
  font-weight: 400;
  text-transform: capitalize;
}
.normal-text {
  font-size: 15px;
  font-weight: 600;
  text-transform: capitalize;
}
.bold-text {
  font-size: 16px;
  font-weight: bold;
}
.score-text {
  font-size: 30px;
  line-height: 30px;
  font-weight: 700;
  /* color: rgba(8, 48, 109, 0.5); */
  color: white;
}

.header {
  --background: #1a2933 !important;
  color: white;
}

.custom-segment {
  background: $background !important;
}

.custom-segment ::before {
  border-left: none;
}

.custom-segment .segment-button-checked {
  color: $primary;
  --indicator-box-shadow: none;
  border-bottom: 2px solid $primary;
}

.bye-card {
  margin-inline: 1.5rem;
  position: relative;
  border-radius: 24px;
  cursor: pointer;
  box-shadow: inset 0 0 0 1000px rgba(117, 125, 138, .25);
  -webkit-box-shadow: inset 0 0 0 1000px rgba(117, 125, 138, .25);
  
  font-family: 'BrownStd-Regular';
  color: white;
  background: transparent;
  .round-text {
    margin-top: 0.5rem;
    margin-bottom: 1rem;
  }
}
.custom-card {
  border-radius: 24px;
  cursor: pointer;
  // box-shadow: inset 0 0 0 1000px rgba(117, 125, 138, .25);
  // -webkit-box-shadow: inset 0 0 0 1000px rgba(117, 125, 138, .25);
  font-family: 'BrownStd-Regular';
  font-size: 1.2rem;
  line-height: 137.4%;
  letter-spacing: 0.02em;
  color: white;
   box-shadow: none;
  background: #171717 !important;
  margin-inline: 1.5rem;
}

.card-content-ios {
  padding: 10px;
}

.custom-card .promoted-header {
  font-family: 'BrownStd-Regular';
  text-align: center;
  font-size: .9rem;
  font-weight: 600;
  text-transform: capitalize;
  line-height: 137.4%;
  letter-spacing: 0.02em;
  color: #2d3a45;
}

.list-ios, .list-md {
  padding: 0;
  background: 0;
}

ion-item {
  --background: #171717 !important;
  --color: white;
}

.custom-segment ion-label {
  margin-bottom: 0;
}

ion-content {
  background: $background;
  --background: $background;
  --offset-bottom: auto!important;
  --overflow: hidden;
  overflow: auto;
}

ion-content::-webkit-scrollbar-track
{
  -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.3);
  background-color: rgba(0,0,0,0.3);
}

ion-content::-webkit-scrollbar
{
  width: .35rem;
  background-color: rgba(0,0,0,0.3);
}

@media only screen and (max-width: 768px) {
  ion-content::-webkit-scrollbar {
    width: .15rem;
    background-color: rgba(0,0,0,0.3);
  }
}

ion-content::-webkit-scrollbar-thumb
{
  background-color: #888;
}


.overlay {
  background: $background;
  // background: $primary;
  padding: 0rem;
}

ion-toolbar, ion-item {
  --border-width: 0 !important;
  --background: $background;
  --color: white;
}

ion-segment-button {
  --background-hover: white !important;
  --color-hover: $highlight !important;
  --color-checked: $highlight !important;
}

.custom-segment {
  border: none;
  /* background: white; */
  border-radius: 0px;
  border-bottom: 1px solid rgba(177, 177, 177, 0.3);
  padding-top: 1rem;
  border: none;
  background: none;
   display: flex;
  // justify-content: center;
  /* border-radius: 1rem !important; */
}

.custom-segment ::before {
  border-left: none;
}

.custom-segment .in-segment {
  color: rgba(177, 177, 177, 1);
  font-size: 1.3rem;
  // background-color: rgba($primary, .7);
}

.custom-segment .segment-button-checked {
  color: rgb(234 68 68);
  border-radius: 0;
  margin-bottom: 0;
  // background-color: rgba($primary, .7);
  color: $highlight;
  font-size: 1.3rem;
  font-weight: 500;
  --indicator-height: 0;
  --indicator-box-shadow: none;
  /* --indicator-color: rgb(234, 68, 68, 0.7);
  --indicator-transform:translate3d(0, 25px, 0) scale3d(.7,1,1); */
  border-bottom: 3px solid $highlight;
}
.custom-segment-ladder {
  // border: none;
  margin: 0 auto;
  margin-top: 1rem;
  display: flex;
  width:70%;
  justify-content: center;
  border-radius: 0.6rem !important;
}

.custom-segment-ladder-button{
  flex-basis: 50%;
}


.custom-segment-ladder ::before {
  border-left: none;
}

.custom-segment-ladder .in-segment {
  color: rgba(177, 177, 177, 1);
  background-color: rgba($primary, .2);
}

.custom-segment-ladder .segment-button-checked {
  color: rgb(234 68 68);
  // margin-bottom: 0;
  background-color: rgba($primary, .7);
  color: $highlight;
  font-weight: 400;
  // --indicator-height: 0;
  // --indicator-box-shadow: none;
  //  --indicator-color: rgb(234, 68, 68, 0.7);
  // --indicator-transform:translate3d(0, 25px, 0) scale3d(.7,1,1); 
  // border-bottom: 3px solid $highlight;
}

.empty-container {
  height: 60vh;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: none;
  -webkit-box-shadow: none;
}

.empty-container ion-card {
  padding: 4rem;
  border-radius: 1rem;
  background: transparent;
  box-shadow: none;
  -webkit-box-shadow: none;
}

.empty-placeholder {
  align-items: flex-start;
  font-family: 'BrownStd-Regular';
  font-style: normal;
  font-weight: bold;
  font-size: 1.2rem;
  line-height: 137.4%;
  /* margin-bottom: 1rem; */
  padding-left: 0;

  letter-spacing: 0.02em;

  color: white;
}

.team-name {
  font-size: 0.8rem;
  white-space: break-spaces;
}

ion-select::part(icon) {
  margin-left: 0.5rem;
  color: white !important;
  opacity: 0.85;
}

.competition-list {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 2rem;
}

.accordion-list {
  width: 80rem;
  // width: 100%;
  margin: 0 1.5rem;
}

.accordion {
  margin-bottom: 10px;
  min-width: 12rem;
  border-radius: 10px;
  overflow: hidden;
  background-color: #17181B ;
  color: white;
}

.accordion-header {
  cursor: pointer;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  background-color: #222;
}

.accordion-content {
  padding: 15px;
}

.team-list-competition {
  margin-top: 1rem;
  margin-bottom: 1rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  padding: 0 1.5rem;
  gap: 1.5rem;
  .team-option {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
}

.rotate-arrow {
  transform: rotate(180deg);
  transition: transform 0.2s ease-in-out;
}

.slide-enter-active, .slide-leave-active {
  transition: all 0.2s ease;
}

.slide-enter, .slide-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>